import type { DirtyItem, LifeOsCollectionName, LifeOsState } from "../types";
import { migrateState } from "./storage";

const EXPORT_SCHEMA_VERSION = 1;
export const MAX_IMPORT_BYTES = 10 * 1024 * 1024;
const collectionNames: LifeOsCollectionName[] = [
  "tasks",
  "projects",
  "goals",
  "habits",
  "notes",
  "transactions",
  "budgets",
  "inbox",
  "events",
  "recurring",
  "accounts",
  "dailyPlans",
  "dailyReviews",
  "weeklyReviews",
  "templates",
  "currencyRates",
  "financeCategories",
];

type ExportPayload = {
  app: "personal-life-os";
  schemaVersion: typeof EXPORT_SCHEMA_VERSION;
  exportedAt: string;
  state: LifeOsState;
};

function withoutSyncQueues(state: LifeOsState): LifeOsState {
  return {
    ...state,
    dirtyItems: [],
    deletedItems: [],
  };
}

export function exportLifeOsState(state: LifeOsState) {
  const payload: ExportPayload = {
    app: "personal-life-os",
    schemaVersion: EXPORT_SCHEMA_VERSION,
    exportedAt: new Date().toISOString(),
    state: withoutSyncQueues(state),
  };

  return JSON.stringify(payload, null, 2);
}

/** Thông điệp lỗi khi tệp không phải bản sao lưu Life OS hợp lệ (hiển thị được cho người dùng). */
export const IMPORT_INVALID_BACKUP_MESSAGE =
  "Tệp không phải bản sao lưu Personal Life OS hợp lệ — không có dữ liệu nào bị thay đổi.";

function isRecordValue(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

/**
 * Parse a backup string into a migrated LifeOsState (no dirty-marking / replace prep).
 * F11: arbitrary valid JSON must NOT coerce into a near-empty state (import would
 * mark everything dirty and push that emptiness over the cloud copy). Accepted:
 *  - the export envelope (`app: "personal-life-os"` + object `state`), or
 *  - a legacy raw state having at least one known collection key as an array.
 * Everything else throws.
 */
export function parseImportedState(raw: string): LifeOsState {
  const parsed: unknown = JSON.parse(raw);
  if (!isRecordValue(parsed)) {
    throw new Error(IMPORT_INVALID_BACKUP_MESSAGE);
  }

  if (parsed.app === "personal-life-os") {
    if (!isRecordValue(parsed.state)) {
      throw new Error(IMPORT_INVALID_BACKUP_MESSAGE);
    }
    return migrateState(parsed.state as Partial<LifeOsState>);
  }

  const hasKnownCollection = collectionNames.some((collection) => Array.isArray(parsed[collection]));
  if (!hasKnownCollection) {
    throw new Error(IMPORT_INVALID_BACKUP_MESSAGE);
  }
  return migrateState(parsed as Partial<LifeOsState>);
}

export function parseLifeOsImport(raw: string): LifeOsState {
  return prepareImportedState(parseImportedState(raw));
}

/**
 * Merge an imported backup INTO the current state instead of replacing it.
 * Union by id per collection: existing items are kept untouched; only items whose
 * id is not already present get added (so re-importing the same file is idempotent
 * — no duplicates). Newly added items are marked dirty so cloud sync pushes them.
 */
export function mergeLifeOsImport(raw: string, current: LifeOsState): LifeOsState {
  const imported = parseImportedState(raw);

  const now = new Date().toISOString();
  const merged = { ...current } as LifeOsState;
  const mergedRec = merged as unknown as Record<LifeOsCollectionName, Array<{ id: string }>>;
  const curRec = current as unknown as Record<LifeOsCollectionName, Array<{ id: string }>>;
  const impRec = imported as unknown as Record<LifeOsCollectionName, Array<{ id: string }>>;
  const dirty: DirtyItem[] = [...current.dirtyItems];

  for (const collection of collectionNames) {
    const existingIds = new Set(curRec[collection].map((item) => item.id));
    const added = impRec[collection].filter((item) => !existingIds.has(item.id));
    mergedRec[collection] = [...added, ...curRec[collection]];
    for (const item of added) dirty.push({ collection, id: item.id, changedAt: now });
  }

  merged.dirtyItems = dirty;
  merged.deletedItems = current.deletedItems;
  merged.updatedAt = now;
  return merged;
}

export function validateLifeOsImportFile(file: Pick<File, "name" | "size" | "type">): string | null {
  if (file.size > MAX_IMPORT_BYTES) {
    return "Backup file is too large";
  }

  const hasJsonName = /\.json$/i.test(file.name);
  const hasJsonType = ["", "application/json", "text/json", "application/octet-stream"].includes(file.type);
  return hasJsonName || hasJsonType ? null : "Invalid backup file";
}

export function prepareImportedState(state: LifeOsState): LifeOsState {
  const now = new Date().toISOString();
  const dirtyItems = collectionNames.flatMap((collection) =>
    state[collection].map<DirtyItem>((item) => ({
      collection,
      id: item.id,
      changedAt: now,
    })),
  );

  return {
    ...state,
    dirtyItems,
    deletedItems: [],
    updatedAt: now,
  };
}
